import type { ForYouFeedItem } from "../../forYou/hooks/useGetForYouRecommendedContentQuery";
import { displayToast } from "../../../components/app/AppToast";
import { SoundEffect } from "../../../interfaces";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  convertArticleToItemData,
  gaEventArticleSave,
} from "../../../tools/analytics/domains/article.analytics";
import { gaEventContentSave } from "../../../tools/analytics/domains/content.analytics";
import { iosPlaySound } from "../../../tools/ios";
import { useGetUserCreatedPlaylistsQuery } from "./useGetUserCreatedPlaylistsQuery";
import { apiClient, useApiRevalidate } from "../../../services/api";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";
import type { SearchArticleItem } from "../../../features/search/hooks/useSearchArticlesQuery";
import type {
  ArticleDetail,
  RelatedArticleCard,
} from "../../../features/article/hooks/useArticleData";

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

export function useSaveArticle(
  article:
    | ArticleDetail
    | RelatedArticleCard
    | SearchArticleItem
    | FeedArticleItem
    | null,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const { playlists: createdPlaylists } = useGetUserCreatedPlaylistsQuery();
  const revalidate = useApiRevalidate();
  const onboardingGuard = useOnboardingGuard();
  const isSaved = createdPlaylists.some((playlist) => {
    return (playlist.playlist ?? []).some((id) => id === article?.id);
  });

  async function toggleSaveToPlaylists(
    article:
      ArticleDetail | RelatedArticleCard | SearchArticleItem | FeedArticleItem,
    { add, remove }: { add: string[]; remove: string[] }
  ) {
    try {
      const slug = article.slug;

      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to save this content. ")
        )
      ) {
        return;
      }

      await apiClient.post("/webapp/private/playlists/batch/article", {
        json: { slug, add, remove },
      });

      if (add.length > 0) {
        iosPlaySound(SoundEffect.SAVE);
        gaEventArticleSave({
          article,
          listName: analyticsListName,
        });
        gaEventContentSave(
          convertArticleToItemData({ article, listName: analyticsListName })
        );
      }

      displayToast(
        add.length > 0
          ? t("Article saved to your collection")
          : t("Article removed from your collection"),
        "success"
      );

      // Non-critical: refresh caches (fire-and-forget)
      void revalidate("/webapp/private/playlists/me");
      // The desktop bookmark reads the Saved list, which counts collections.
      void revalidate("/webapp/private/saved");
      void revalidate("/webapp/public/articles/:slug", { param: { slug } });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return {
    isSaved,
    toggleSaveToPlaylists,
  };
}
